import { apiFetch } from "../lib/api";

/** Has bars in the store — chartable right now. */
export interface StoredInstrumentOut {
  id: string;
  display: string;
  symbol: string;
  exchange: string;
  instrument_type: string;
  expiry: string | null;
  strike: string | null;
  timeframes: string[];
  total_bars: number;
  last_ts: string | null;
  /** Contract size / price step, joined from the broker master. Defaults to
   *  1 / "0.05" for indices, equities, and expired contracts the master no
   *  longer lists. Carried so the backtest and deployment forms can size an
   *  F&O position straight from a search result. */
  lot_size: number;
  tick_size: string;
}

/** Exists at the broker but isn't downloaded yet — needs a backfill first. */
export interface MasterInstrumentOut {
  id: string;
  display: string;
  symbol: string;
  exchange: string;
  instrument_type: string;
  expiry: string | null;
  strike: string | null;
  lot_size: number;
  tick_size: string;
  name: string | null;
  broker: string;
}

export interface InstrumentSearchResponse {
  query: string;
  tokens: string[];
  stored: StoredInstrumentOut[];
  master: MasterInstrumentOut[];
  stored_truncated: boolean;
  master_truncated: boolean;
  /** null => no instruments master has ever been snapshotted. */
  master_snapshot_date: string | null;
}

export interface InstrumentCoverageOut {
  timeframe: string;
  bar_count: number;
  first_ts: string | null;
  last_ts: string | null;
}

/** Typeahead. `q` is split on whitespace and every token must match (AND).
 *  `limit` is per section, so stored results can't crowd out the download list. */
export async function searchInstruments(
  q: string,
  limit = 10,
): Promise<InstrumentSearchResponse> {
  return apiFetch<InstrumentSearchResponse>(
    `/instruments/search?q=${encodeURIComponent(q)}&limit=${limit}`,
  );
}

/** One stored instrument by id. Needed to restore a deep-linked or bookmarked
 *  chart URL now that the full instrument list no longer lives in the browser. */
export async function resolveInstrument(id: string): Promise<StoredInstrumentOut> {
  return apiFetch<StoredInstrumentOut>(
    `/instruments/resolve?id=${encodeURIComponent(id)}`,
  );
}

export async function getInstrumentCoverage(
  id: string,
): Promise<InstrumentCoverageOut[]> {
  return apiFetch<InstrumentCoverageOut[]>(
    `/instruments/coverage?id=${encodeURIComponent(id)}`,
  );
}
